// ── GuestAccessPage ✦ — venue arrival doorway ───────────────────
//
// Public route: /access/:slug. Mobile-first, walnut/gold/ivory,
// editorial. Designed so a guest can scan the venue QR and land in
// the live atmosphere of the event in under 10 seconds — no auth,
// no signup prompts, no dashboard energy.
//
// Tone (locked):
//   "Welcome to the evening ✦", "Find your place ✦", "Your table
//   is waiting ✦". Banned: "search", "lookup", "submit", "portal",
//   "dashboard", "manage".
//
// Failure handling: never hard-reject. If the name doesn't resolve,
// the guest still enters — seating-specific cards hidden, gallery /
// guestbook / booth-os still surfaced if entitled.
import React, { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { getGuestProducts, isBoothOSPackage, isLeaversEdition } from '../../shared/packageGuestProducts';
import { saveGuestSession, loadGuestSession } from '../../shared/guestSession';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Walnut · gold · ivory palette — venue-native, calm.
const INK = '#1f1409';
const INK_SOFT = '#3b2e22';
const IVORY = '#fbf6ec';
const PARCHMENT = '#f5efdf';
const GOLD = '#a4824a';
const GOLD_SOFT = 'rgba(164,130,74,0.22)';
const HAIRLINE = 'rgba(31,20,9,0.14)';
const FONT_DISPLAY = "'Cormorant Garamond', 'Playfair Display', Georgia, serif";
const FONT_BODY = "'Inter', system-ui, sans-serif";

const PoweredFooter = () => (
  <p
    data-testid="guest-access-powered-footer"
    style={{
      margin: '40px 0 12px', textAlign: 'center',
      color: INK_SOFT, opacity: 0.55,
      fontFamily: FONT_BODY, fontSize: 11, letterSpacing: '0.18em',
      textTransform: 'uppercase',
    }}
  >
    Powered quietly by EventDrop ✦
  </p>
);

const AccessCard = ({ title, sub, onClick, testid }) => (
  <button
    onClick={onClick}
    data-testid={testid}
    style={{
      display: 'block', width: '100%',
      textAlign: 'left', cursor: 'pointer',
      padding: '20px 22px', marginBottom: 12,
      borderRadius: 4,
      background: IVORY,
      border: `1px solid ${HAIRLINE}`,
      boxShadow: '0 1px 0 rgba(31,20,9,0.04)',
      fontFamily: FONT_BODY, color: INK,
      transition: 'transform .12s ease, border-color .12s ease',
    }}
    onMouseEnter={(e) => { e.currentTarget.style.borderColor = GOLD_SOFT; }}
    onMouseLeave={(e) => { e.currentTarget.style.borderColor = HAIRLINE; }}
  >
    <p style={{
      margin: 0, fontFamily: FONT_DISPLAY, fontSize: 22, fontWeight: 500,
      color: INK, letterSpacing: '-0.01em', lineHeight: 1.2,
    }}>
      {title}
    </p>
    <p style={{
      margin: '6px 0 0', fontSize: 13, color: INK_SOFT, lineHeight: 1.55,
    }}>
      {sub}
    </p>
  </button>
);

const GuestAccessPage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Resolution state
  const [name, setName] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [resolved, setResolved] = useState(null);  // {found, guest, features, event}
  const [softMessage, setSoftMessage] = useState('');
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/access/${slug}`);
        const ev = r.data?.event;
        setEvent(ev);
        // iter188 — restore the resolved hub from sessionStorage so
        // pressing browser-back from any product card returns the
        // guest to their welcome hub (not the empty name input).
        const cached = loadGuestSession(slug);
        const isLeavers = ev && isLeaversEdition(ev);
        let resolvedFromCache = false;
        // iter199 — Skip the cached-session lookup for Leavers Night.
        // Leavers is collective, not invited (iter196). The cache can
        // only carry a synthetic identifier from a prior booth_os or
        // access entry on this device; resurrecting it personalises
        // the welcome line with a foreign-looking id like
        // `booth_event_…` and reads in the room as "redirected to a
        // different event". For Leavers we always fall straight
        // through to the collective synthesis below.
        if (cached?.guest_id && !isLeavers) {
          try {
            const lookup = await axios.post(
              `${API}/access/${slug}/lookup`,
              { guest_name: cached.guest_name || cached.guest_id },
            );
            if (lookup?.data) {
              setResolved(lookup.data);
              resolvedFromCache = true;
            }
          } catch { /* welcome screen fallback is fine */ }
        }
        if (!resolvedFromCache && isLeavers) {
          // iter196 — Leavers Night is collective, not invited.
          // Bypass the name gate entirely: the QR scan IS the
          // entry. We synthesise a resolved-hub state with the
          // edition's full features map so the guest lands
          // directly in the participation grid (Remember Us +
          // Upload + Pocket Booth + Memory Wall + …).
          //
          // No name is collected by default — name capture, if it
          // ever returns, must be optional + soft (Belief 5:
          // friction must approach zero for schools). Also runs
          // when a cached-session lookup failed on a Leavers event
          // so a stale cache never strands a student on the name
          // gate.
          setResolved({
            found: true,
            guest: {},
            features: {
              // No seating / menu / floorplan for schools — the
              // wedding-era access-page entitlements stay off.
              seating: false,
              menu: false,
              floorplan: false,
            },
            event: ev,
          });
        }
      } catch (err) {
        setError(err?.response?.data?.detail || 'No evening found here ✦');
      } finally { setLoading(false); }
    })();
  }, [slug]);

  // iter214 ✦ — Multi-upload. Same architecture as BoothLauncher's
  // handleUpload — sequential POSTs, soft progress for batches,
  // partial-success honesty. The standalone access path uses the
  // guest-tagged source identifier so the operator can see which
  // guest each upload came from.
  const handleUpload = async (e) => {
    const fileList = Array.from(e.target.files || []);
    if (!fileList.length) return;
    const evtForUpload = resolved?.event || event;
    if (!evtForUpload?.event_id) return;
    const safeGuest = (resolved?.guest?.guest_id)
      || `access_${(name || '').trim().toLowerCase().replace(/\s+/g, '_') || 'guest'}`;
    setUploading(true);
    let ok = 0;
    let failed = 0;
    try {
      for (let i = 0; i < fileList.length; i += 1) {
        const file = fileList[i];
        try {
          const fd = new FormData();
          fd.append('file', file);
          await axios.post(
            `${API}/events/${evtForUpload.event_id}/photos?source=guest_${encodeURIComponent(safeGuest)}`,
            fd,
            { headers: { 'Content-Type': 'multipart/form-data' } },
          );
          ok += 1;
          if (fileList.length > 1) {
            setSoftMessage(`Saving ${ok} of ${fileList.length} ✦`);
          }
        } catch {
          failed += 1;
        }
      }
      if (failed === 0) {
        setSoftMessage(
          fileList.length === 1
            ? 'Saved into tonight ✦'
            : `${ok} memories saved into tonight ✦`,
        );
      } else if (ok === 0) {
        setSoftMessage("The upload didn't quite land — please try again ✦");
      } else {
        setSoftMessage(`${ok} saved, ${failed} didn't land — try those again ✦`);
      }
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const enter = async (e) => {
    e?.preventDefault?.();
    if (!name.trim() || submitting) return;
    setSubmitting(true); setSoftMessage('');
    try {
      const r = await axios.post(`${API}/access/${slug}/lookup`, { guest_name: name.trim() });
      setResolved(r.data);
      // iter187 — persist tab-scoped guest identity so browser-back
      // from any product card returns to this access screen with the
      // guest already resolved (not the empty name lookup). Save
      // even on soft-fallback so the back-button doesn't dump the
      // user at the welcome input again.
      if (r.data?.guest) {
        const safe = name.trim().toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, '');
        saveGuestSession({
          slug,
          guest_id: r.data.guest.guest_id || `access_${safe || 'guest'}`,
          guest_name: r.data.guest.name || name.trim(),
        });
      }
      // Soft fallback messaging — do NOT hard-reject (UX rule §5)
      if (!r.data?.found) {
        setSoftMessage("We couldn't quietly place that name tonight ✦ — please try the name exactly as it appeared on the invitation.");
      }
    } catch (err) {
      setSoftMessage(err?.response?.data?.detail || "Something went quiet — please try again ✦");
    } finally {
      setSubmitting(false);
    }
  };

  // Loading
  if (loading) {
    return (
      <div data-testid="guest-access-loading" style={{
        minHeight: '100vh', background: PARCHMENT, color: INK_SOFT,
        fontFamily: FONT_BODY, display: 'flex', alignItems: 'center',
        justifyContent: 'center', padding: 24, fontSize: 13,
      }}>
        ✦
      </div>
    );
  }

  // Disabled / not found
  if (error || !event) {
    return (
      <div data-testid="guest-access-not-found" style={{
        minHeight: '100vh', background: PARCHMENT, color: INK,
        fontFamily: FONT_BODY, padding: '64px 24px', textAlign: 'center',
      }}>
        <p style={{ fontFamily: FONT_DISPLAY, fontSize: 36, margin: 0, color: INK }}>
          ✦
        </p>
        <p style={{ marginTop: 18, fontFamily: FONT_DISPLAY, fontSize: 22, color: INK }}>
          No evening found here
        </p>
        <p style={{ marginTop: 8, fontSize: 13, color: INK_SOFT, lineHeight: 1.6 }}>
          The link may have changed. Please check with your host.
        </p>
        <PoweredFooter />
      </div>
    );
  }

  const couple = event.couple_names || event.event_name || 'the evening';

  // ── State 2 — resolved guest hub ────────────────────────────
  if (resolved) {
    const g = resolved.guest || {};
    const f = resolved.features || {};
    // event field comes from resolved payload (carries package_type +
    // features for the canonical matrix). Fall back to top-level event
    // if the resolved payload is missing it.
    const evt = resolved.event || event;
    const leavers = isLeaversEdition(evt);
    // iter196 — Leavers Night welcome reads as the *room*, not a
    // personal greeting. No "Welcome there ✦" awkwardness when the
    // student didn't (and shouldn't have to) type a name.
    const welcomeLine = leavers && !g.name
      ? 'Welcome to the evening ✦'
      : `Welcome ${g.name?.split(' ')[0] || 'there'} ✦`;

    return (
      <div data-testid="guest-access-page" data-state="resolved" style={{
        minHeight: '100vh', background: PARCHMENT, color: INK,
        fontFamily: FONT_BODY, padding: '36px 18px 48px',
      }}>
        <div style={{ maxWidth: 480, margin: '0 auto' }}>
          {/* Welcome line — direct, no confirmation noise */}
          <p
            data-testid="guest-access-welcome-line"
            style={{
              margin: 0, fontFamily: FONT_DISPLAY, fontSize: 32, lineHeight: 1.15,
              color: INK, letterSpacing: '-0.01em',
            }}
          >
            {welcomeLine}
          </p>

          {/* Seating callout — only when we have it */}
          {(g.table || g.seat) && (
            <div
              data-testid="guest-access-seating"
              style={{
                margin: '20px 0 8px', padding: '18px 20px',
                borderTop: `1px solid ${GOLD_SOFT}`,
                borderBottom: `1px solid ${GOLD_SOFT}`,
                background: IVORY,
              }}
            >
              <p style={{
                margin: 0, fontSize: 11, fontWeight: 700, letterSpacing: '0.28em',
                textTransform: 'uppercase', color: GOLD,
              }}>
                Your place
              </p>
              <p style={{
                margin: '8px 0 0', fontFamily: FONT_DISPLAY, fontSize: 26,
                color: INK, letterSpacing: '-0.01em',
              }}>
                {g.table}{g.seat ? ` · ${g.seat}` : ''}
              </p>
              {g.notes && (
                <p style={{ margin: '6px 0 0', fontSize: 13, color: INK_SOFT, fontStyle: 'italic' }}>
                  {g.notes}
                </p>
              )}
            </div>
          )}

          {/* Soft fallback message when name didn't resolve */}
          {softMessage && (
            <p
              data-testid="guest-access-soft-message"
              style={{
                margin: '20px 0', padding: '14px 16px',
                background: IVORY, border: `1px solid ${HAIRLINE}`,
                color: INK_SOFT, fontSize: 13, lineHeight: 1.6,
              }}
            >
              {softMessage}
            </p>
          )}

          {/* Editorial divider line */}
          <p style={{
            margin: '28px 0 14px', fontFamily: FONT_DISPLAY, fontSize: 18,
            color: INK, fontStyle: 'italic',
          }}>
            Tonight's atmosphere ✦
          </p>

          {/* Seating-specific cards (not part of the canonical product
              matrix — they're access-page-only entitlements). */}
          {f.seating && f.floorplan && (
            <AccessCard
              title="View seating plan"
              sub="Find your place at a glance ✦"
              onClick={() => window.open(evt.floorplan_url || event.floorplan_url, '_blank')}
              testid="guest-access-card-floorplan"
            />
          )}
          {f.menu && (
            <AccessCard
              title="View the menu"
              sub="What's being served tonight ✦"
              onClick={() => window.open(evt.menu_url || event.menu_url, '_blank')}
              testid="guest-access-card-menu"
            />
          )}

          {/* iter188 — Booth OS Consolidation
              Room + Archive enter through Booth OS. Replace the
              dashboard-style product grid with a single editorial
              CTA. Keepsake (and any legacy package) keeps the grid. */}
          {isBoothOSPackage(evt) ? (
            <button
              type="button"
              data-testid="guest-access-enter-tonight"
              onClick={() => {
                // Re-save session with entry='booth_os' so every
                // "Back to Dashboard" inside a guest product on this
                // event returns to the Booth OS root.
                const safe = (name || '').trim().toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, '');
                const sessionGuestId = (resolved?.guest?.guest_id)
                  || `access_${safe || 'guest'}`;
                saveGuestSession({
                  slug,
                  guest_id: sessionGuestId,
                  guest_name: resolved?.guest?.name || name.trim() || '',
                  entry: 'booth_os',
                  event_id: evt.event_id,
                });
                navigate(`/booth-os/${evt.event_id}`);
              }}
              style={{
                display: 'block', width: '100%',
                textAlign: 'left', cursor: 'pointer',
                padding: '28px 26px', marginTop: 8, marginBottom: 12,
                borderRadius: 6,
                background: IVORY,
                border: `1px solid ${GOLD_SOFT}`,
                boxShadow: '0 6px 24px rgba(31,20,9,0.08)',
                fontFamily: FONT_BODY, color: INK,
              }}
            >
              <p style={{
                margin: 0, fontFamily: FONT_DISPLAY, fontSize: 30, fontWeight: 500,
                color: INK, letterSpacing: '-0.01em', lineHeight: 1.15,
              }}>
                Enter Tonight ✦
              </p>
              <p style={{
                margin: '10px 0 0', fontSize: 14, color: INK_SOFT,
                fontFamily: FONT_BODY, lineHeight: 1.5, fontStyle: 'italic',
              }}>
                Everything from tonight lives here quietly ✦
              </p>
            </button>
          ) : (
            <>
              {/* Keepsake (and legacy fallback) — lightweight product grid. */}
              {getGuestProducts(evt).map((product) => (
                <AccessCard
                  key={product.id}
                  title={product.id === 'qr_upload' && uploading ? 'Uploading ✦' : product.title}
                  sub={product.subtitle}
                  testid={`guest-access-card-${product.id}`}
                  onClick={() => {
                    if (product.action === 'open-uploader') {
                      fileRef.current?.click();
                      return;
                    }
                    if (typeof product.route === 'function') {
                      navigate(product.route(slug, evt));
                    }
                  }}
                />
              ))}
              <input
                ref={fileRef}
                type="file"
                accept="image/*,video/*"
                multiple
                style={{ display: 'none' }}
                onChange={handleUpload}
                data-testid="guest-access-upload-input"
              />
            </>
          )}

          <PoweredFooter />
        </div>
      </div>
    );
  }

  // ── State 1 — welcome + name input ──────────────────────────
  return (
    <div data-testid="guest-access-page" data-state="welcome" style={{
      minHeight: '100vh', background: PARCHMENT, color: INK,
      fontFamily: FONT_BODY, padding: '64px 24px 48px',
    }}>
      <div style={{ maxWidth: 460, margin: '0 auto', textAlign: 'center' }}>
        <p
          data-testid="guest-access-eyebrow"
          style={{
            margin: 0, fontSize: 11, fontWeight: 700, letterSpacing: '0.32em',
            textTransform: 'uppercase', color: GOLD,
          }}
        >
          ✦ Tonight
        </p>
        {/* Welcome-line consolidation (iter 165): when the host has
            written a custom intro, it BECOMES the H1 — we don't render
            a second generic greeting underneath. Otherwise the default
            "Welcome to <couple> ✦" stands alone. Either way: one calm
            greeting line, never two. */}
        <p
          data-testid="guest-access-title"
          style={{
            margin: '14px 0 6px', fontFamily: FONT_DISPLAY, fontSize: 38,
            lineHeight: 1.15, color: INK, letterSpacing: '-0.015em',
          }}
        >
          {event.intro ? event.intro : `Welcome to ${couple} ✦`}
        </p>

        <form
          onSubmit={enter}
          data-testid="guest-access-form"
          style={{ marginTop: 36, display: 'flex', flexDirection: 'column', gap: 14 }}
        >
          <p style={{
            margin: 0, fontFamily: FONT_DISPLAY, fontSize: 18,
            color: INK, fontStyle: 'italic',
          }}>
            Find your place ✦
          </p>
          <input
            type="text"
            placeholder="Your name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            autoFocus
            autoComplete="name"
            data-testid="guest-access-name-input"
            style={{
              padding: '14px 16px',
              fontFamily: FONT_BODY, fontSize: 16,
              background: IVORY, border: `1px solid ${HAIRLINE}`,
              borderRadius: 4, color: INK,
              outline: 'none', textAlign: 'center',
              transition: 'border-color .15s ease',
            }}
            onFocus={(e) => { e.target.style.borderColor = GOLD; }}
            onBlur={(e) => { e.target.style.borderColor = HAIRLINE; }}
          />
          <button
            type="submit"
            disabled={!name.trim() || submitting}
            data-testid="guest-access-enter-button"
            style={{
              padding: '14px 18px', borderRadius: 4,
              background: INK, color: IVORY, border: 'none',
              fontFamily: FONT_BODY, fontSize: 14, fontWeight: 700,
              letterSpacing: '0.14em', textTransform: 'uppercase',
              cursor: submitting ? 'wait' : 'pointer',
              opacity: !name.trim() || submitting ? 0.6 : 1,
            }}
          >
            {submitting ? 'One moment ✦' : 'Enter the night ✦'}
          </button>
        </form>

        <PoweredFooter />
      </div>
    </div>
  );
};

export default GuestAccessPage;
